/**
 * Type definitions for subscription plan features
 * These control what features are available to stores based on their plan
 */

export interface PlanFeatures {
  // Core features
  rides_per_month: number;
  max_riders_per_ride: number;
  
  // Content features
  feed_posts: number; // 0 = disabled, -1 = unlimited
  
  // Marketing features
  emails_per_month: number; // 0 = disabled
  ai_suggestions: boolean;
  
  // Loyalty features
  loyalty_points_manager: boolean;
  loyalty_multipliers: boolean;
  
  // Analytics features
  basic_analytics: boolean;
  advanced_analytics: boolean;
  garage_analytics: boolean;
  
  // Club features
  clubs_enabled: boolean;
  max_clubs: number;
  
  // Support features
  priority_support: boolean;
  
  // Additional feature flags
  [key: string]: number | boolean | string | undefined;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  priceYearly: number;
  features: PlanFeatures;
  isActive: boolean;
}

export interface Subscription {
  id: string;
  status: 'ACTIVE' | 'TRIALING' | 'PENDING' | 'CANCELLED' | 'PAST_DUE' | 'EXPIRED';
  plan: SubscriptionPlan;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export interface StoreWithPlan {
  id: string;
  name: string;
  location: string | null;
  city: string | null;
  planFeatures: PlanFeatures | null;
  planName: string | null;
  isTrial: boolean;
  trialEndsAt: string | null;
  subscription: Subscription | null;
}

/**
 * Default plan features for stores without a subscription (Starter plan)
 */
export const DEFAULT_PLAN_FEATURES: PlanFeatures = {
  rides_per_month: 4,
  max_riders_per_ride: 20,
  feed_posts: 0,
  emails_per_month: 0,
  ai_suggestions: false,
  loyalty_points_manager: false,
  loyalty_multipliers: false,
  basic_analytics: true,
  advanced_analytics: false,
  garage_analytics: false,
  clubs_enabled: false,
  max_clubs: 0,
  priority_support: false,
};

/**
 * Check if a feature is enabled for a plan
 */
export function isFeatureEnabled(
  planFeatures: PlanFeatures | null,
  feature: keyof PlanFeatures
): boolean {
  if (!planFeatures) return false;
  const value = planFeatures[feature];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0 || value === -1; // -1 = unlimited
  return !!value;
}

/**
 * Get the limit for a numeric feature
 */
export function getFeatureLimit(
  planFeatures: PlanFeatures | null,
  feature: keyof PlanFeatures
): number {
  if (!planFeatures) return 0;
  const value = planFeatures[feature];
  if (typeof value === 'number') return value;
  return 0;
}
